import type { EnergySeriesResult } from '../../components/charts/EnergyUseSupabase';
import { MUNICIPAL_ELECTRICITY_NONRESIDENTIAL_LABEL } from './municipalUtilityElectricity';
import {
  formatElectricityTooltipPrimary,
  formatGwhAxisTickFromKwh,
} from '../../utils/formatElectricityGwh';
import {
  burbankCapCommercialElectricityKwh,
  burbankCapResidentialElectricityKwh,
} from '../capAnchoredActivity/canonical/burbankCapSectorReference';
import { buildEia861CanonicalElectricitySeries } from './buildEia861CanonicalSeries';
import { BURBANK_GEOID } from './bwpObservations';
import type {
  Eia861ActivityStatus,
  Eia861CanonicalElectricityYear,
  Eia861PilotUtilityConfig,
  Eia861SectorYearValue,
} from './types';

const SECTOR_BASE_COLOR = {
  residential: '#1e40af',
  commercial: '#0d9488',
} as const;

const BAR_LAYOUT = {
  categoryPercentage: 0.88,
  barPercentage: 0.82,
  maxBarThickness: 32,
};

const CAP_BAR_COLORS = {
  residential: { bg: 'rgba(217, 119, 6, 0.92)', border: '#92400e' },
  commercial: { bg: 'rgba(194, 65, 12, 0.88)', border: '#7c2d12' },
} as const;

const EIA_STACK = 'eia-sector';
const CAP_STACK = 'cap-sector';

export function isBurbankEia861PilotConfig(config: Eia861PilotUtilityConfig): boolean {
  const norm = config.geoid.length === 6 ? `0${config.geoid}` : config.geoid;
  return norm === BURBANK_GEOID;
}

function chartYearsForPilot(years: number[], config: Eia861PilotUtilityConfig): number[] {
  if (!isBurbankEia861PilotConfig(config)) return years;
  const extended = new Set(years);
  extended.add(2024);
  return Array.from(extended).sort((a, b) => a - b);
}

function buildCapComparisonBarDataset(years: number[], sector: 'residential' | 'commercial') {
  const getter =
    sector === 'residential' ? burbankCapResidentialElectricityKwh : burbankCapCommercialElectricityKwh;
  const data = years.map((y) => getter(y));
  if (!data.some((v) => v != null)) return null;
  const palette = sector === 'residential' ? CAP_BAR_COLORS.residential : CAP_BAR_COLORS.commercial;
  return {
    type: 'bar' as const,
    label:
      sector === 'residential'
        ? 'Published CAP residential (Rincon 2024)'
        : 'Published CAP commercial (not EIA nonresidential)',
    data,
    backgroundColor: palette.bg,
    borderColor: palette.border,
    borderWidth: 2,
    stack: CAP_STACK,
    ...BAR_LAYOUT,
  };
}

function statusLegendLabel(status: Eia861ActivityStatus): string {
  switch (status) {
    case 'observed_eia861':
      return 'EIA-861 observed';
    case 'interpolated_between_eia_observations':
      return 'Linear interpolation';
    case 'flat_before_first_eia_observation':
    case 'flat_after_last_eia_observation':
      return 'Flat endpoint';
    default:
      return 'Unknown';
  }
}

function sectorValue(row: Eia861CanonicalElectricityYear, sector: 'residential' | 'commercial'): Eia861SectorYearValue {
  return sector === 'residential' ? row.residential : row.commercial;
}

function pushStatusValue(
  status: Eia861ActivityStatus,
  kwh: number,
  buckets: Record<Eia861ActivityStatus, number[]>
): void {
  for (const key of Object.keys(buckets) as Eia861ActivityStatus[]) {
    buckets[key].push(key === status ? kwh : 0);
  }
}

function buildStatusStackDatasets(
  canonical: Eia861CanonicalElectricityYear[],
  sector: 'residential' | 'commercial',
  stackId: string = EIA_STACK
) {
  const buckets: Record<Eia861ActivityStatus, number[]> = {
    observed_eia861: [],
    interpolated_between_eia_observations: [],
    flat_before_first_eia_observation: [],
    flat_after_last_eia_observation: [],
  };

  for (const row of canonical) {
    const s = sectorValue(row, sector);
    pushStatusValue(s.status, s.kwh, buckets);
  }

  const base = SECTOR_BASE_COLOR[sector];
  const flatEndpointData = buckets.flat_before_first_eia_observation.map(
    (v, i) => v + buckets.flat_after_last_eia_observation[i]
  );

  const datasets: Array<{
    type: 'bar';
    label: string;
    data: (number | null)[];
    backgroundColor: string;
    borderColor: string;
    borderWidth: number;
    stack: string;
    categoryPercentage: number;
    barPercentage: number;
    maxBarThickness: number;
  }> = [];

  if (buckets.observed_eia861.some((v) => v > 0)) {
    datasets.push({
      type: 'bar',
      label: 'EIA-861 observed',
      data: buckets.observed_eia861,
      backgroundColor: base,
      borderColor: '#1e3a5f',
      borderWidth: 1,
      stack: stackId,
      ...BAR_LAYOUT,
    });
  }

  if (buckets.interpolated_between_eia_observations.some((v) => v > 0)) {
    const interpolatedFill =
      sector === 'residential' ? 'rgba(59, 130, 246, 0.72)' : 'rgba(45, 212, 191, 0.72)';
    datasets.push({
      type: 'bar',
      label: 'Linear interpolation',
      data: buckets.interpolated_between_eia_observations,
      backgroundColor: interpolatedFill,
      borderColor: '#1e3a5f',
      borderWidth: 1,
      stack: stackId,
      ...BAR_LAYOUT,
    });
  }

  if (flatEndpointData.some((v) => v > 0)) {
    datasets.push({
      type: 'bar',
      label: 'Flat endpoint',
      data: flatEndpointData,
      backgroundColor: `${base}55`,
      borderColor: '#64748b',
      borderWidth: 2,
      stack: stackId,
      ...BAR_LAYOUT,
    });
  }

  return datasets;
}

function eiaStatusTooltipLines(s: Eia861SectorYearValue): string[] {
  const lines = [
    `Status: ${statusLegendLabel(s.status)}`,
    `Utility: ${s.utilityName} (EIA ID ${s.eiaUtilityId})`,
  ];
  if (s.anchorYears) {
    lines.push(`Anchors: ${s.anchorYears[0]}–${s.anchorYears[1]}`);
  }
  if (s.sourceFile) {
    lines.push(`Source year file: ${s.sourceFile}`);
  }
  return lines;
}

function buildEia861BarChartOptions(
  shortTitle: string,
  stacked: boolean,
  canonical: Eia861CanonicalElectricityYear[],
  sector: 'residential' | 'commercial' | 'total',
  options?: { includeCapComparison?: boolean }
) {
  const includeCap = options?.includeCapComparison ?? false;
  const rowByYear = new Map(canonical.map((r) => [r.year, r]));

  return {
    type: 'bar',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 10,
          usePointStyle: true,
          pointStyle: 'rect',
          font: { size: 12 },
        },
      },
      title: {
        display: true,
        text: shortTitle,
        font: { size: 15, weight: '600' },
        padding: { bottom: 4 },
      },
      tooltip: {
        callbacks: {
          title: (items: Array<{ label: string }>) => `Year ${items[0]?.label ?? ''}`,
          label: (ctx: { dataset: { label?: string }; parsed: { y: number } }) => {
            const v = Number(ctx.parsed.y);
            if (!Number.isFinite(v) || v <= 0) return '';
            return `${ctx.dataset.label ?? 'Value'}: ${formatElectricityTooltipPrimary(v)}`;
          },
          afterBody: (items: Array<{ label: string; dataIndex: number }>) => {
            const year = Number(items[0]?.label);
            const row = rowByYear.get(year);
            if (!row) return [];
            if (sector === 'total') {
              const res = row.residential;
              const com = row.commercial;
              const total = row.totalKwh;
              const delta = res.kwh + com.kwh - total;
              return [
                `Residential: ${formatElectricityTooltipPrimary(res.kwh)}`,
                `Nonresidential: ${formatElectricityTooltipPrimary(com.kwh)}`,
                `Total: ${formatElectricityTooltipPrimary(total)}`,
                ...(Math.abs(delta) > 1
                  ? [`Reconciliation delta: ${Math.round(delta).toLocaleString()} kWh`]
                  : []),
                ...eiaStatusTooltipLines(res).map((l) => `Residential — ${l}`),
                ...eiaStatusTooltipLines(com).map((l) => `Commercial — ${l}`),
              ];
            }
            const s = sectorValue(row, sector as 'residential' | 'commercial');
            const scope =
              sector === 'residential' ? 'Residential' : MUNICIPAL_ELECTRICITY_NONRESIDENTIAL_LABEL;
            const lines = [`Scope: ${scope}`, ...eiaStatusTooltipLines(s)];
            if (includeCap) {
              const capKwh =
                sector === 'residential'
                  ? burbankCapResidentialElectricityKwh(year)
                  : burbankCapCommercialElectricityKwh(year);
              if (capKwh != null) {
                lines.push(`Published CAP (Rincon 2024): ${formatElectricityTooltipPrimary(capKwh)}`);
              }
            }
            return lines;
          },
        },
      },
    },
    scales: {
      x: {
        stacked,
        title: { display: true, text: 'Year', font: { size: 12 } },
        ticks: {
          maxRotation: 45,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: 12,
          font: { size: 12 },
        },
      },
      y: {
        stacked,
        beginAtZero: true,
        title: { display: true, text: 'GWh', font: { size: 12 } },
        ticks: {
          callback: (v: string | number) => formatGwhAxisTickFromKwh(Number(v)),
          font: { size: 12 },
        },
      },
    },
    layout: { padding: { bottom: 16, top: 4 } },
  };
}

export function buildElectricitySeriesFromEia861Pilot(
  _location: string,
  sector: 'residential' | 'commercial',
  years: number[],
  config: Eia861PilotUtilityConfig
): EnergySeriesResult {
  const chartYears = chartYearsForPilot(years, config);
  const burbankCapOverlay = isBurbankEia861PilotConfig(config);
  const canonical = buildEia861CanonicalElectricitySeries(config, chartYears);
  const shortTitle =
    sector === 'residential'
      ? 'Residential electricity consumption'
      : 'Nonresidential electricity consumption';

  const datasets = buildStatusStackDatasets(canonical, sector, EIA_STACK);
  if (burbankCapOverlay) {
    const capDs = buildCapComparisonBarDataset(chartYears, sector);
    if (capDs) datasets.push(capDs);
  }

  const chartData = {
    labels: chartYears,
    datasets,
  };

  const last = canonical[canonical.length - 1];
  const latestSector = sector === 'residential' ? last.residential : last.commercial;

  return {
    chartData,
    chartOptions: buildEia861BarChartOptions(shortTitle, true, canonical, sector, {
      includeCapComparison: burbankCapOverlay,
    }),
    latestYear: last.year,
    latestValue: latestSector.kwh,
    allDataObserved: canonical.every((r) => {
      const st = sector === 'residential' ? r.residential.status : r.commercial.status;
      return st === 'observed_eia861';
    }),
    eia861PilotActive: true,
    eia861ChartSubtitle: burbankCapOverlay
      ? 'Blue/teal stacks: EIA-861 BWP (2507). Orange bars: published CAP (Rincon 2024) at inventory years — not added to EIA totals.'
      : 'Burbank Water and Power retail sales reported to EIA-861',
    eia861NonresLabel: MUNICIPAL_ELECTRICITY_NONRESIDENTIAL_LABEL,
  };
}

export function buildElectricityStackedTotalFromEia861Pilot(
  years: number[],
  config: Eia861PilotUtilityConfig
): Pick<EnergySeriesResult, 'chartData' | 'chartOptions' | 'latestYear' | 'latestValue'> {
  const chartYears = chartYearsForPilot(years, config);
  const burbankCapOverlay = isBurbankEia861PilotConfig(config);
  const canonical = buildEia861CanonicalElectricitySeries(config, chartYears);
  const resData = canonical.map((r) => r.residential.kwh);
  const comData = canonical.map((r) => r.commercial.kwh);
  const datasets: Array<Record<string, unknown>> = [
    {
      type: 'bar',
      label: 'EIA-861 residential',
      data: resData,
      backgroundColor: SECTOR_BASE_COLOR.residential,
      borderColor: '#1e3a5f',
      borderWidth: 1,
      stack: 'eia-total',
      ...BAR_LAYOUT,
    },
    {
      type: 'bar',
      label: `EIA-861 ${MUNICIPAL_ELECTRICITY_NONRESIDENTIAL_LABEL.toLowerCase()}`,
      data: comData,
      backgroundColor: SECTOR_BASE_COLOR.commercial,
      borderColor: '#115e59',
      borderWidth: 1,
      stack: 'eia-total',
      ...BAR_LAYOUT,
    },
  ];
  if (burbankCapOverlay) {
    const capRes = buildCapComparisonBarDataset(chartYears, 'residential');
    const capCom = buildCapComparisonBarDataset(chartYears, 'commercial');
    if (capRes) datasets.push(capRes);
    if (capCom) datasets.push(capCom);
  }
  const chartData = {
    labels: chartYears,
    datasets,
  };

  const last = canonical[canonical.length - 1];

  return {
    chartData,
    chartOptions: buildEia861BarChartOptions('Total electricity consumption', true, canonical, 'total', {
      includeCapComparison: burbankCapOverlay,
    }),
    latestYear: last.year,
    latestValue: last.totalKwh,
  };
}

/** First year suitable for “change since …” headline (not flat endpoint). */
export function eia861FirstTrendBaselineYear(
  canonical: Eia861CanonicalElectricityYear[],
  sector: 'residential' | 'commercial' | 'total'
): { year: number; kwh: number } | null {
  for (const row of canonical) {
    const status =
      sector === 'total'
        ? row.residential.status === 'observed_eia861' && row.commercial.status === 'observed_eia861'
          ? 'observed_eia861'
          : row.residential.status
        : sectorValue(row, sector).status;
    if (
      status === 'observed_eia861' ||
      status === 'interpolated_between_eia_observations'
    ) {
      const kwh =
        sector === 'total'
          ? row.totalKwh
          : sector === 'residential'
            ? row.residential.kwh
            : row.commercial.kwh;
      return { year: row.year, kwh };
    }
  }
  return null;
}
